// A script's outline - its top-level declarations (`fn`, `record`,
// `constant`, `variable`), `uses` and `import` lines - for the Code
// workspace's Outline panel, Monaco's own "Go to Symbol" quick pick and
// breadcrumb bar (`registerDocumentSymbolProvider` in `monacoAdapter.ts`),
// and the file list's per-script function count. Godot's script panel
// puts exactly this list under the open-files list (its "Outline" /
// "Filter Methods" section), and Roblox's Script Function Filter (Alt+F)
// is the same list as a search box - both are the reference for what
// belongs here: declarations only, never statements.
//
// Walked over `@hollow/script`'s own published AST, so a declaration the
// parser accepted is exactly what shows here - never a regex approximation
// that could list a `fn` inside a string literal. Returns plain data (no
// `monaco-editor` import - see `tokenize.ts`'s own doc comment for why that
// boundary matters in this directory).

import { parse, Source } from '@hollow/script';
import type { Program, TopLevelItem } from '@hollow/script';
import type { FnDecl, RecordDecl, TypeNode } from './astTypes.js';
import { typeNodeToString } from './typeText.js';

export type OutlineItemKind = 'function' | 'record' | 'constant' | 'variable' | 'uses' | 'import';

export interface OutlineItem {
  readonly kind: OutlineItemKind;
  readonly name: string;
  /** `fn greet(name: String): String`, `record Task = { ... }`, etc. - the
   * declaration's own head, for a tooltip or a symbol picker's detail. */
  readonly detail: string;
  readonly exported: boolean;
  /** 1-based, matching Monaco's own position convention. */
  readonly line: number;
  readonly column: number;
  readonly endLine: number;
  readonly endColumn: number;
}

type ConstantDecl = Extract<TopLevelItem, { kind: 'ConstantDecl' }>;
type VariableDecl = Extract<TopLevelItem, { kind: 'VariableDecl' }>;

function fnDetail(fn: FnDecl): string {
  const params = fn.parameters.map((p) => `${p.name.name}: ${typeNodeToString(p.type)}`).join(', ');
  const ret: TypeNode | undefined = fn.returnType;
  return `fn ${fn.name.name}(${params})${ret ? `: ${typeNodeToString(ret)}` : ''}`;
}

function recordDetail(record: RecordDecl): string {
  return `record ${record.name.name}`;
}

function bindingDetail(keyword: 'constant' | 'variable', decl: ConstantDecl | VariableDecl): string {
  return decl.type ? `${keyword} ${decl.name.name}: ${typeNodeToString(decl.type)}` : `${keyword} ${decl.name.name}`;
}

/** The outline of `source`, in source order. Never throws: `parse` never
 * does (spec.md gate 2), and a file mid-edit still yields whatever parsed. */
export function computeHollowScriptOutline(source: string): readonly OutlineItem[] {
  const { program } = parse(source);
  return outlineOfProgram(program, source);
}

export function outlineOfProgram(program: Program, source: string): readonly OutlineItem[] {
  const positions = new Source(source, 'main.hws');
  const items: OutlineItem[] = [];

  function push(kind: OutlineItemKind, name: string, detail: string, exported: boolean, span: { start: number; end: number }): void {
    const start = positions.positionAt(span.start);
    const end = positions.positionAt(span.end);
    items.push({ kind, name, detail, exported, line: start.line, column: start.column, endLine: end.line, endColumn: end.column });
  }

  for (const uses of program.uses) {
    for (const name of uses.names) push('uses', name.name, `uses ${name.name}`, false, name.span);
  }
  for (const imported of program.imports) {
    // The import path is a plain string literal (spec.md §2 forbids
    // interpolation in it), so its text is the literal's source slice minus
    // the quotes - read from `source` rather than re-assembling parts.
    const path = source.slice(imported.from.span.start + 1, Math.max(imported.from.span.start + 1, imported.from.span.end - 1));
    for (const name of imported.names) push('import', name.name, `import ${name.name} from "${path}"`, false, name.span);
  }
  for (const item of program.topLevel) {
    const exported = item.kind === 'ExportedDecl';
    const decl = item.kind === 'ExportedDecl' ? item.declaration : item;
    switch (decl.kind) {
      case 'FnDecl':
        push('function', decl.name.name, fnDetail(decl), exported, decl.span);
        break;
      case 'RecordDecl':
        push('record', decl.name.name, recordDetail(decl), exported, decl.span);
        break;
      case 'ConstantDecl':
        push('constant', decl.name.name, bindingDetail('constant', decl), exported, decl.span);
        break;
      case 'VariableDecl':
        push('variable', decl.name.name, bindingDetail('variable', decl), exported, decl.span);
        break;
      default:
        break;
    }
  }
  return items;
}
